import { useEffect, useRef, useState } from 'react'
import { toast } from 'react-toastify'

import { useThunkDispatch } from '@campaignhub/react-hooks'

import { aiEditAsset, getAiEditStatus } from '@redux/modules/prompt'
import { loadAsset } from '@redux/modules/asset'

const AI_ASSET_INCLUDES = 'comments,file,histories,file.filePreviewRequests'
const AI_POLL_INTERVAL = 3000
const AI_POLL_TIMEOUT = 3 * 60 * 1000
const AI_POLL_MAX_FAILURES = 3

const useAiVersionGeneration = (assetId) => {
  const dispatch = useThunkDispatch()

  const [generating, setGenerating] = useState(false)
  const pollRef = useRef({ active: false, timeoutId: null })

  const stopPolling = () => {
    pollRef.current.active = false
    if (pollRef.current.timeoutId){
      clearTimeout(pollRef.current.timeoutId)
      pollRef.current.timeoutId = null
    }
  }

  const stopGenerating = () => {
    stopPolling()
    setGenerating(false)
  }

  const pollAiEdit = (requestId) => {
    stopPolling()
    pollRef.current.active = true
    const deadline = Date.now() + AI_POLL_TIMEOUT
    let consecutiveFailures = 0

    const tick = async () => {
      if (!pollRef.current.active) return

      const { success, data, status } = await dispatch(getAiEditStatus(requestId))
      if (!pollRef.current.active) return

      if (!success){
        if (status === 404){
          stopGenerating()
          toast.info('New version is generating. Please refresh the page shortly.')
          return
        }

        consecutiveFailures += 1
        if (consecutiveFailures >= AI_POLL_MAX_FAILURES){
          stopGenerating()
          toast.error('Couldn\'t check the generation status. Please refresh the page shortly.')
          return
        }

        pollRef.current.timeoutId = setTimeout(tick, AI_POLL_INTERVAL)
        return
      }

      consecutiveFailures = 0

      if (data?.status === 'Completed'){
        await dispatch(loadAsset(assetId, { includes: AI_ASSET_INCLUDES }))
        if (!pollRef.current.active) return
        stopGenerating()
        return
      }

      if (data?.status === 'Failed'){
        stopGenerating()
        toast.error(data?.error || 'AI generation failed. Please try again.')
        return
      }

      if (Date.now() > deadline){
        stopGenerating()
        toast.error('AI generation is taking longer than expected. Please refresh the page shortly.')
        return
      }

      pollRef.current.timeoutId = setTimeout(tick, AI_POLL_INTERVAL)
    }

    tick()
  }

  const generate = async ({ prompt, promptKey, customPrompt }) => {
    const { success, data } = await dispatch(aiEditAsset(assetId, prompt, promptKey, customPrompt))

    if (success){
      setGenerating(true)

      if (data?.requestId){
        pollAiEdit(data.requestId)
      } else {
        setGenerating(false)
        toast.info('New version is generating. Please refresh the page shortly.')
      }
    }

    return { success }
  }

  useEffect(() => () => stopPolling(), [])

  return { generating, generate, stopGenerating }
}

export default useAiVersionGeneration
